import { getCategoryProfile } from "../category-profiles";
import { DEFAULT_CATEGORY_TAXONOMY_CONFIG, createCategoryOptions, getCategoryDisplayBreadcrumb, type CategoryTaxonomyConfig } from "../category-taxonomy";
import { CONSULTATION_PRODUCT_TARGET_ID, createFormCatalogFromSteps, DEFAULT_FORM_CONFIG, mergeFormCatalog, targetIdForField, targetIdForFieldOption, targetIdForGuard, targetIdForRepeater, targetIdForRepeaterField, targetIdForRepeaterFieldOption, targetIdForStep, targetIdForUpload, type FormCatalogItem, type FormCatalogKind, type FormConfigDiagnostic, type FormConfigVersion, type FormRule, type FormRuleCondition, type FormRuleEffect } from "../form-config";
import { applyCategoryPreset, buildProfileAdjustedIntakeSteps, createDefaultIntakeData, getEffectiveIntakeSchema } from "../form-schema";
import { getIntakePartForStepId, groupIntakeItemsByPart, type IntakePartDefinition } from "../form-schema/parts";
import type { FieldOption, FileUploadDefinition, IntakeCategoryId, IntakeData, IntakeDomain, IntakeField, IntakeStep, RepeaterField } from "../types";

const CATEGORY_OPTIONS = createCategoryOptions(DEFAULT_CATEGORY_TAXONOMY_CONFIG);
const ALL_FORM_CONFIG_CATEGORIES: Array<Exclude<IntakeCategoryId, "">> = CATEGORY_OPTIONS.map((option) => option.value);
const REPRESENTATIVE_FORM_CONFIG_CATEGORIES: Array<Exclude<IntakeCategoryId, "">> = ["medical_dental", "beauty_hair", "telecom_living_support", "fnb_restaurant"];
export const FORM_CONFIG_DRAFT_STORAGE_KEY = "meet-the-world:admin-form-config-draft:v1";
export const FORM_CONFIG_SELECTED_GROUP_STORAGE_KEY = "meet-the-world:admin-form-config-selected-group:v1";
const FORM_CONFIG_EDITOR_RULE_PREFIX = "admin-editor";

export type PreviewToggles = {
  adminAccountNeeded: boolean;
  touristConvenience: boolean;
  targetConcern: boolean;
  exclusiveBenefit: boolean;
};

export type EditorScopeType = "global" | "domain" | "category" | "contractType";

export type TargetStatus = {
  visible: boolean;
  label: string;
  required?: boolean;
  optionCount?: number;
};

export const DEFAULT_PREVIEW_TOGGLES: PreviewToggles = {
  adminAccountNeeded: true,
  touristConvenience: false,
  targetConcern: false,
  exclusiveBenefit: true,
};

export function buildStepMatrixTargets(catalog: FormCatalogItem[], selectedStepId: string, search: string): FormCatalogItem[] {
  const byId = new Map(catalog.map((item) => [item.id, item]));
  const stepTargetId = `step.${selectedStepId}`;
  const step = byId.get(stepTargetId);
  if (!step) return [];

  const childrenByParent = new Map<string, FormCatalogItem[]>();
  for (const item of catalog) {
    if (!item.parentId) continue;
    const children = childrenByParent.get(item.parentId) ?? [];
    children.push(item);
    childrenByParent.set(item.parentId, children);
  }

  const rows: FormCatalogItem[] = [step];
  const appendOptionChildren = (parentId: string) => {
    rows.push(...(childrenByParent.get(parentId) ?? []).filter((child) => child.kind === "option"));
  };

  for (const child of childrenByParent.get(stepTargetId) ?? []) {
    if (child.kind === "option") continue;
    rows.push(child);
    if (child.kind === "field") appendOptionChildren(child.id);
    if (child.kind === "repeater") {
      for (const repeaterField of (childrenByParent.get(child.id) ?? []).filter((item) => item.kind === "repeaterField")) {
        rows.push(repeaterField);
        appendOptionChildren(repeaterField.id);
      }
    }
  }

  const keyword = search.trim().toLowerCase();
  if (!keyword) return rows;
  return rows.filter((item, index) => index === 0 || searchableCatalogText(item).includes(keyword));
}

export function searchableCatalogText(item: FormCatalogItem): string {
  return [item.label, item.checkboxText, item.id, item.fieldPath, item.description].filter(Boolean).join(" ").toLowerCase();
}

export function matrixTargetLabel(target: FormCatalogItem): string {
  return target.kind === "option" ? `↳ ${target.label}` : target.label;
}

export function matrixRowClass(targets: FormCatalogItem[], index: number, selectedStepId: string): string {
  const target = targets[index];
  const previous = targets[index - 1];
  const classes: string[] = [`kind-${target.kind}`];
  if (target.id === `step.${selectedStepId}`) classes.push("step-row");
  if (target.kind === "option") classes.push("child-row");
  if (index > 1 && target.parentId === `step.${selectedStepId}` && previous?.kind === "option") classes.push("group-start");
  return classes.join(" ");
}

export function isTextEntryTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tagName = target.tagName.toLowerCase();
  return target.isContentEditable || tagName === "input" || tagName === "textarea" || tagName === "select";
}

export function nextStepId(currentStepId: string, stepTargets: FormCatalogItem[], direction: 1 | -1): string {
  const stepIds = stepTargets.map((target) => target.id.replace(/^step\./, ""));
  if (!stepIds.length) return currentStepId;
  const currentIndex = stepIds.indexOf(currentStepId);
  if (currentIndex < 0) return stepIds[0];
  return stepIds[(currentIndex + direction + stepIds.length) % stepIds.length];
}

export function orderStepTargets(stepTargets: FormCatalogItem[], effectiveSteps: IntakeStep[]): FormCatalogItem[] {
  const effectiveRank = new Map(effectiveSteps.map((step, index) => [`step.${step.id}`, index]));
  const fallbackRank = new Map(stepTargets.map((target, index) => [target.id, index]));
  return [...stepTargets].sort((left, right) => {
    const leftRank = effectiveRank.get(left.id) ?? 1000 + (fallbackRank.get(left.id) ?? 0);
    const rightRank = effectiveRank.get(right.id) ?? 1000 + (fallbackRank.get(right.id) ?? 0);
    return leftRank - rightRank || left.label.localeCompare(right.label);
  });
}

export type StepTargetPart = { part: IntakePartDefinition; stepTargets: FormCatalogItem[] };

// The UI groups catalog targets only; every target retains its existing raw step ID.
export function groupStepTargetsByPart(stepTargets: FormCatalogItem[]): StepTargetPart[] {
  return groupIntakeItemsByPart(stepTargets, (target) => target.id.replace(/^step\./, ""))
    .map(({ part, items }) => ({ part, stepTargets: items }));
}

export function stepOptionLabel(stepTarget: FormCatalogItem, effectiveSteps: IntakeStep[], fallbackIndex: number): string {
  const stepId = stepTarget.id.replace(/^step\./, "");
  const effectiveStep = effectiveSteps.find((step) => step.id === stepId);
  const stepNumber = effectiveStep?.eyebrow?.match(/\d+/)?.[0] ?? String(fallbackIndex + 1);
  return `${stepNumber}. ${stepTarget.label}`;
}

export function nextQuickEffect(target: FormCatalogItem, status: TargetStatus | undefined): Partial<FormRuleEffect> {
  if (target.kind === "option" || target.checkboxText) return { visible: !status };
  if (!supportsRequired(target.kind)) return { visible: !status };
  if (!status) return { visible: true, required: false };
  if (status.required) return { visible: false };
  return { visible: true, required: true };
}

export function quickPillClass(target: FormCatalogItem, status: TargetStatus | undefined): string {
  if (!status) return "admin-config-pill off";
  if (!target.checkboxText && supportsRequired(target.kind) && status.required) return "admin-config-pill required";
  return "admin-config-pill on";
}

export function UserSurveyStepPreview({ step, selectedStepId, categoryLabelText }: { step: IntakeStep | null; selectedStepId: string; categoryLabelText: string }) {
  const part = getIntakePartForStepId(selectedStepId);
  const partLabel = part ? `파트 ${part.label} · ${part.title}` : `step.${selectedStepId}`;
  if (!step) {
    return (
      <div className="admin-user-preview-card hidden">
        <span>{partLabel}</span>
        <strong>이 단계는 현재 카테고리에서 숨김 처리되었습니다.</strong>
        <small>step.{selectedStepId}</small>
      </div>
    );
  }

  return (
    <div className="admin-user-preview-card">
      <header className="admin-user-preview-head">
        <span>{categoryLabelText}</span>
        <em>{partLabel}</em>
        <h4>{step.title}</h4>
        <p>{step.description}</p>
      </header>
      <div className="admin-user-preview-body">
        {step.fields?.map((field) => <SurveyFieldPreview field={field} key={field.path} />)}
        {step.repeaters?.map((repeater) => <SurveyRepeaterPreview repeater={repeater} key={repeater.id} />)}
        {step.fileUploads?.map((upload) => <SurveyUploadPreview upload={upload} key={upload.id} />)}
        {step.tips?.map((tip) => (
          <aside className={`admin-user-preview-tip ${tip.kind ?? "info"}`} key={`${tip.title}-${tip.body}`}>
            <strong>{tip.title}</strong>
            <p>{tip.body}</p>
          </aside>
        ))}
      </div>
    </div>
  );
}

export function SurveyFieldPreview({ field }: { field: IntakeField }) {
  const value = stringValue(field.defaultValue);
  return (
    <label className={`admin-user-preview-field type-${field.type}`}>
      <span>{field.label} {field.readOnly ? <em>고정</em> : field.required ? <em>필수</em> : null}</span>
      {renderSurveyFieldInput(field, value)}
      {field.helperText && <small>{field.helperText}</small>}
    </label>
  );
}

export function renderSurveyFieldInput(field: IntakeField, value: string) {
  if (field.readOnly) return <output>{value || "-"}{field.valueSuffix ?? ""}</output>;
  if (field.type === "textarea") return <textarea value={value} placeholder={field.placeholder} rows={Math.min(field.rows ?? 3, 4)} readOnly />;
  if (field.type === "select") {
    return (
      <select value={value || field.options?.[0]?.value || ""} disabled>
        {(field.options ?? []).map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </select>
    );
  }
  if (field.type === "radio") {
    return (
      <div className="admin-user-preview-choice-list">
        {(field.options ?? []).map((option, index) => (
          <div className="admin-user-preview-choice" key={option.value}>
            <i>{value ? option.value === value ? "●" : "○" : index === 0 ? "●" : "○"}</i>
            <span><strong>{option.label}</strong>{option.description && <small>{option.description}</small>}</span>
          </div>
        ))}
      </div>
    );
  }
  if (field.type === "checkbox") {
    return <div className="admin-user-preview-check">□ {field.checkboxText ?? "동의합니다"}</div>;
  }
  if (field.type === "multiCheckbox") {
    return (
      <div className="admin-user-preview-choice-list compact">
        {(field.options ?? []).slice(0, 8).map((option) => <div className="admin-user-preview-check" key={option.value}>□ {option.label}</div>)}
        {(field.options?.length ?? 0) > 8 && <small>외 {(field.options?.length ?? 0) - 8}개 옵션</small>}
      </div>
    );
  }
  return <input value={value} placeholder={field.placeholder} readOnly />;
}

export function SurveyRepeaterPreview({ repeater }: { repeater: RepeaterField }) {
  return (
    <section className="admin-user-preview-repeater">
      <div>
        <strong>{repeater.label}</strong>
        {repeater.description && <p>{repeater.description}</p>}
      </div>
      <button type="button" disabled>{repeater.addLabel}</button>
      <div className="admin-user-preview-repeater-fields">
        {repeater.fields.slice(0, 6).map((field) => <SurveyFieldPreview field={{ ...field, path: `${repeater.path}.${field.path}` }} key={field.path} />)}
        {repeater.fields.length > 6 && <small>외 {repeater.fields.length - 6}개 입력칸</small>}
      </div>
    </section>
  );
}

export function SurveyUploadPreview({ upload }: { upload: FileUploadDefinition }) {
  return (
    <article className="admin-user-preview-upload">
      <strong>{upload.label} {upload.required && <em>필수</em>}</strong>
      {upload.description && <p>{upload.description}</p>}
      <span>파일 선택하기</span>
    </article>
  );
}

export function EffectiveStepPreview({ step }: { step: IntakeStep }) {
  return (
    <details className="admin-effective-step" open={step.id === "contract" || step.id === "company"}>
      <summary><strong>{step.title}</strong><small>step.{step.id}</small></summary>
      <div>
        {step.fields?.map((field) => (
          <article className="admin-preview-option" key={field.path}>
            <strong>{field.label} {field.required && <em>필수</em>}</strong>
            <small>{targetIdForField(field)}</small>
            {field.options?.length ? <small>옵션 {field.options.map((option) => option.label).join(" / ")}</small> : null}
          </article>
        ))}
        {step.repeaters?.map((repeater) => (
          <article className="admin-preview-option" key={repeater.id}>
            <strong>{repeater.label}</strong>
            <small>{targetIdForRepeater(repeater)} · fields {repeater.fields.map((field) => field.label).join(" / ")}</small>
          </article>
        ))}
        {step.fileUploads?.map((upload) => (
          <article className="admin-preview-option" key={upload.id}>
            <strong>{upload.label} {upload.required && <em>필수</em>}</strong>
            <small>{targetIdForUpload(upload)}</small>
          </article>
        ))}
      </div>
    </details>
  );
}

export function DiagnosticsList({ diagnostics }: { diagnostics: FormConfigDiagnostic[] }) {
  return (
    <div className="admin-diagnostic-list">
      {diagnostics.length === 0 ? (
        <div className="admin-alert success">검증 오류가 없습니다.</div>
      ) : diagnostics.map((diagnostic) => (
        <div className={`admin-alert ${diagnostic.level === "error" ? "error" : "warning"}`} key={`${diagnostic.code}-${diagnostic.ruleId ?? ""}-${diagnostic.targetId ?? "global"}-${diagnostic.message}`}>
          <strong>{diagnostic.code}</strong> {diagnostic.message}
        </div>
      ))}
    </div>
  );
}

export function loadStoredString(key: string, fallback: string): string {
  try {
    if (typeof window === "undefined") return fallback;
    return localStorage.getItem(key) || fallback;
  } catch {
    return fallback;
  }
}

export function loadFormConfigDraft(): FormConfigVersion {
  try {
    if (typeof window === "undefined") return DEFAULT_FORM_CONFIG;
    const raw = localStorage.getItem(FORM_CONFIG_DRAFT_STORAGE_KEY);
    if (!raw) return DEFAULT_FORM_CONFIG;
    const parsed = JSON.parse(raw) as FormConfigVersion;
    if (!parsed.versionId || !Array.isArray(parsed.rules)) return DEFAULT_FORM_CONFIG;
    return normalizeEditorConfig({ ...DEFAULT_FORM_CONFIG, ...parsed, catalog: mergeFormCatalog(DEFAULT_FORM_CONFIG.catalog, parsed.catalog ?? []) });
  } catch {
    return DEFAULT_FORM_CONFIG;
  }
}

export function createAllScenarioCatalog(categoryIds: Array<Exclude<IntakeCategoryId, "">> = ALL_FORM_CONFIG_CATEGORIES): FormCatalogItem[] {
  const catalogs = categoryIds.map((categoryId) => {
    const data = applyCategoryPreset(createDefaultIntakeData(), categoryId);
    return createFormCatalogFromSteps(buildProfileAdjustedIntakeSteps(data));
  });
  return mergeFormCatalog(DEFAULT_FORM_CONFIG.catalog, ...catalogs);
}

export function createScenarioData(categoryId: Exclude<IntakeCategoryId, "">, toggles: PreviewToggles, config: FormConfigVersion = DEFAULT_FORM_CONFIG): IntakeData {
  const data = applyCategoryPreset(createDefaultIntakeData(), categoryId, config);
  return {
    ...data,
    reservation: { ...data.reservation, adminAccountNeeded: toggles.adminAccountNeeded },
    content: {
      ...data.content,
      sellingPointTags: toggles.touristConvenience ? appendLineValue(data.content.sellingPointTags, "관광객 방문 편의") : removeLineValue(data.content.sellingPointTags, "관광객 방문 편의"),
      targetCustomers: toggles.targetConcern ? appendLineValue(data.content.targetCustomers, "특정 고민이 있는 고객") : removeLineValue(data.content.targetCustomers, "특정 고민이 있는 고객"),
      creatripExclusiveBenefitStatus: toggles.exclusiveBenefit ? "있음" : "없음",
    },
  };
}

export function createFormConfigPreview(categoryId: Exclude<IntakeCategoryId, "">, config: FormConfigVersion, data = createScenarioData(categoryId, DEFAULT_PREVIEW_TOGGLES, config), taxonomy: CategoryTaxonomyConfig = DEFAULT_CATEGORY_TAXONOMY_CONFIG) {
  const effective = getEffectiveIntakeSchema(data, config);
  return {
    label: categoryLabel(categoryId, taxonomy),
    effective,
  };
}

export function filterCatalogTargets(catalog: FormCatalogItem[], search: string): FormCatalogItem[] {
  const keyword = search.trim().toLowerCase();
  const sorted = [...catalog].sort((left, right) => catalogSortKey(left).localeCompare(catalogSortKey(right)));
  if (!keyword) return sorted;
  return sorted.filter((item) => searchableCatalogText(item).includes(keyword));
}

export function flattenEffectiveTargets(steps: IntakeStep[]): Map<string, TargetStatus> {
  const map = new Map<string, TargetStatus>();
  for (const step of steps) {
    map.set(targetIdForStep(step), { visible: true, label: step.title });
    for (const field of step.fields ?? []) {
      const fieldId = targetIdForField(field);
      map.set(fieldId, { visible: true, label: field.label, required: Boolean(field.required), optionCount: field.options?.length });
      for (const option of field.options ?? []) map.set(targetIdForFieldOption(field, option), { visible: true, label: option.label });
    }
    for (const guard of step.guards ?? []) map.set(targetIdForGuard(guard), { visible: true, label: guard.label });
    for (const repeater of step.repeaters ?? []) {
      map.set(targetIdForRepeater(repeater), { visible: true, label: repeater.label });
      for (const field of repeater.fields) {
        const fieldId = targetIdForRepeaterField(repeater, field);
        map.set(fieldId, { visible: true, label: field.label, required: Boolean(field.required), optionCount: field.options?.length });
        for (const option of field.options ?? []) map.set(targetIdForRepeaterFieldOption(repeater, field, option), { visible: true, label: option.label });
      }
    }
    for (const upload of step.fileUploads ?? []) map.set(targetIdForUpload(upload), { visible: true, label: upload.label, required: Boolean(upload.required) });
  }
  return map;
}

export function findEditorRule(rules: FormRule[], targetId: string, condition: FormRuleCondition): FormRule | null {
  const ruleId = editorRuleId(targetId, condition);
  return rules.find((rule) => rule.id === ruleId) ?? null;
}

export function editorRuleId(targetId: string, condition: FormRuleCondition): string {
  return `${FORM_CONFIG_EDITOR_RULE_PREFIX}.${conditionSlug(condition)}.${targetId.replace(/[^a-zA-Z0-9_-]+/g, "_")}`;
}

export function createScopeCondition(scopeType: EditorScopeType, data: IntakeData): FormRuleCondition {
  const profile = getCategoryProfile(data.category.categoryId);
  if (scopeType === "global" || !profile) return {};
  if (scopeType === "domain") return { domain: profile.domain };
  if (scopeType === "contractType") return { contractType: data.contract.contractType };
  return { categoryId: profile.id };
}

export function scopeLabel(scopeType: EditorScopeType, data: IntakeData): string {
  const profile = getCategoryProfile(data.category.categoryId);
  if (scopeType === "global" || !profile) return "전체 공통";
  if (scopeType === "domain") return `${profile.domainLabel} 전체`;
  if (scopeType === "contractType") return `계약=${data.contract.contractType || "미선택"}`;
  return `${profile.domainLabel} · ${profile.label}`;
}

export function normalizeEditorConfig(config: FormConfigVersion): FormConfigVersion {
  const rulesById = new Map(DEFAULT_FORM_CONFIG.rules.map((rule) => [rule.id, rule]));
  for (const rule of config.rules) rulesById.set(rule.id, rule);
  return {
    ...DEFAULT_FORM_CONFIG,
    ...config,
    catalog: mergeFormCatalog(DEFAULT_FORM_CONFIG.catalog, config.catalog ?? []),
    rules: Array.from(rulesById.values()).map((rule) => (
      rule.id.startsWith(FORM_CONFIG_EDITOR_RULE_PREFIX)
        ? { ...rule, effect: cleanEffect(rule.effect, rule.targetId === CONSULTATION_PRODUCT_TARGET_ID ? ["defaultValue"] : []) }
        : rule
    )),
  };
}

export type ConsultationSeedMode = "default" | "custom" | "none";

export function getConsultationSeedMode(defaultValue: FormRuleEffect["defaultValue"]): ConsultationSeedMode {
  if (defaultValue === undefined) return "default";
  return String(defaultValue).trim() === "" ? "none" : "custom";
}

// "기본 문구"는 rule을 지워 카테고리 기본값으로 돌리고, "사용 안 함"은 빈 문자열로 명시한다.
export function createConsultationSeedEffect(mode: ConsultationSeedMode, optionName: string, categoryDefaultName: string): Partial<FormRuleEffect> {
  if (mode === "default") return { defaultValue: undefined };
  if (mode === "none") return { defaultValue: "" };
  return { defaultValue: optionName.trim() || categoryDefaultName };
}

// keepEmptyKeys: 빈 문자열 자체가 의미를 갖는 effect(예: 상담 상품 사용 안 함)를 지키기 위해 쓴다.
export function cleanEffect(effect: FormRuleEffect, keepEmptyKeys: Array<keyof FormRuleEffect> = []): FormRuleEffect {
  const normalized = { ...effect };
  if (normalized.visible === false && normalized.required === true) delete normalized.required;
  return Object.fromEntries(Object.entries(normalized).filter(([key, value]) => {
    if (value === "") return keepEmptyKeys.includes(key as keyof FormRuleEffect);
    if (value === undefined) return false;
    if (Array.isArray(value) && value.length === 0) return false;
    return true;
  })) as FormRuleEffect;
}

export function toggleAllowedOption(current: string[] | undefined, options: FormCatalogItem[], optionId: string, checked: boolean): string[] | undefined {
  const allIds = options.map((option) => option.id);
  const next = new Set(current ?? allIds);
  if (checked) next.add(optionId);
  else next.delete(optionId);
  const nextIds = allIds.filter((id) => next.has(id));
  return nextIds.length === allIds.length ? undefined : nextIds;
}

export function isOptionAllowed(current: string[] | undefined, optionId: string, options: FormCatalogItem[]): boolean {
  if (!current) return true;
  if (current.length === 0) return false;
  const allOptionIds = new Set(options.map((option) => option.id));
  return allOptionIds.has(optionId) && current.includes(optionId);
}

export function appendLineValue(value: string, item: string): string {
  const items = new Set(value.split("\n").map((line) => line.trim()).filter(Boolean));
  items.add(item);
  return Array.from(items).join("\n");
}

export function removeLineValue(value: string, item: string): string {
  return value.split("\n").map((line) => line.trim()).filter((line) => line && line !== item).join("\n");
}

export function supportsRequired(kind: FormCatalogKind): boolean {
  return ["field", "repeaterField", "upload"].includes(kind);
}

export function supportsLabel(kind: FormCatalogKind): boolean {
  return ["step", "field", "guard", "option", "upload", "repeater", "repeaterField"].includes(kind);
}

export function supportsText(kind: FormCatalogKind, key: "description" | "helperText" | "placeholder" | "defaultValue"): boolean {
  if (key === "description") return ["step", "guard", "option", "upload", "repeater"].includes(kind);
  if (key === "helperText" || key === "placeholder" || key === "defaultValue") return ["field", "repeaterField"].includes(kind);
  return false;
}

export function supportsCheckboxText(target: FormCatalogItem): boolean {
  return ["field", "repeaterField"].includes(target.kind) && Boolean(target.checkboxText);
}

export function triStateValue(value: boolean | undefined): string {
  if (value === undefined) return "inherit";
  return value ? "true" : "false";
}

export function parseTriState(value: string): boolean | undefined {
  if (value === "inherit") return undefined;
  return value === "true";
}

export function stringValue(value: unknown): string {
  return value === undefined || value === null ? "" : String(value);
}

export function kindLabel(kind: FormCatalogKind): string {
  const labels: Record<FormCatalogKind, string> = {
    step: "Step",
    field: "문항",
    option: "옵션",
    upload: "첨부",
    repeater: "반복그룹",
    repeaterField: "반복문항",
    guard: "동작",
  };
  return labels[kind];
}

export function formatTargetStatus(target: FormCatalogItem, status: TargetStatus): string {
  if (target.kind === "guard") return "띄움";
  if (target.checkboxText) return "보임";
  return status.required ? "필수" : "보임";
}

export function formatCondition(condition: FormRuleCondition): string {
  if (!Object.keys(condition).length) return "전체 공통";
  return JSON.stringify(condition);
}

export function conditionSlug(condition: FormRuleCondition): string {
  if (!Object.keys(condition).length) return "global";
  if (condition.categoryId) return `category_${[condition.categoryId].flat().join("_")}`;
  if (condition.domain) return `domain_${[condition.domain].flat().join("_")}`;
  if (condition.contractType) return `contract_${[condition.contractType].flat().join("_")}`;
  return "condition";
}

export function catalogSortKey(item: FormCatalogItem): string {
  const order: Record<FormCatalogKind, number> = { step: 0, field: 1, guard: 2, option: 3, repeater: 4, repeaterField: 5, upload: 6 };
  return `${item.parentId ?? item.id}:${order[item.kind]}:${item.id}`;
}

export function categoryLabel(categoryId: Exclude<IntakeCategoryId, "">, taxonomy: CategoryTaxonomyConfig = DEFAULT_CATEGORY_TAXONOMY_CONFIG): string {
  return getCategoryDisplayBreadcrumb(categoryId, taxonomy) || categoryId;
}
